import React, { forwardRef, useEffect, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useDispatch, useSelector } from "react-redux";
import { easing } from "maath";
import { useGSAP } from "@gsap/react";
import {
  setActiveCamera,
  setCurrentState,
  setInTrans,
  stateMachine,
} from "../../store/reducers/progressionReducer";
import gsap from "gsap";
import { PerspectiveCamera } from "@react-three/drei";
import { LoopOnce, Quaternion } from "three";
import controlsStyle from "../../UI/Controls/Controls.module.css";

export const CameraController = forwardRef(function ({ actions, mixer }, ref) {
  const { camera } = useThree();
  const currentModule = useSelector((state) => state.progression.currentModule);
  const [isMobile, setIsMobile] = useState(false);
  const currentPos = useRef({ x: 0, y: 1.9, z: 10 });
  const mobilePos = useRef({ x: 0, y: 1.7, z: 12 });
  const currentRot = useRef(new THREE.Euler(0, 0, 0));
  const pointer = useRef({ x: 0, y: 0 });
  const dispatch = useDispatch();
  const nextState = useSelector((state) => state.progression.nextState);
  const inTrans = useSelector((state) => state.progression.inTrans);
  const token = useSelector((state) => state.progression.token);
  const activeCamera = useSelector((state) => state.progression.activeCamera);

  useEffect(() => {
    const handleMouseMove = (event) => {
      pointer.current.x = (event.clientX / window.innerWidth) * 2 - 1;
      pointer.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
    };

    const handleTouchMove = (event) => {
      if (event.touches.length > 0) {
        pointer.current.x = (event.touches[0].clientX / window.innerWidth) * 2 - 1;
        pointer.current.y = -(event.touches[0].clientY / window.innerHeight) * 2 + 1;
      }
    };

    function resize() {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      if (mobile) {
        adjustCameraForMobile();
      } else {
        adjustCameraForDesktop();
      }
    }

    resize();

    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("touchmove", handleTouchMove);

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);

  const adjustCameraForMobile = () => {
    if (!inTrans && activeCamera !== "IntroCamera") {
      const currentStateName = nextState !== "none" ? nextState : activeCamera;
      const currentState = stateMachine[currentStateName];

      if (currentState && currentState.mobileCamera) {
        const mobileConfig = currentState.mobileCamera;

        gsap.to(camera.position, {
          x: mobileConfig.position[0],
          y: mobileConfig.position[1],
          z: mobileConfig.position[2],
          duration: 0.5,
          ease: "power2.out",
          onComplete: () => {
            currentPos.current = {
              x: mobileConfig.position[0],
              y: mobileConfig.position[1],
              z: mobileConfig.position[2]
            };
            mobilePos.current = { ...currentPos.current };
          }
        });

        if (mobileConfig.quaternion) {
          const mobileQuat = new Quaternion(...mobileConfig.quaternion);
          const initialQuat = new Quaternion(...camera.quaternion);

          const animation = gsap.to({}, {
            duration: 0.5,
            ease: "circ.out",
            onUpdate: function () {
              camera.quaternion.slerpQuaternions(initialQuat, mobileQuat, animation.progress());
            },
            onComplete: function () {
              const finalRot = camera.rotation;
              currentRot.current = new THREE.Euler(finalRot.x, finalRot.y, finalRot.z);
            }
          });
        }
      } else {
        gsap.to(camera.position, {
          x: mobilePos.current.x,
          y: mobilePos.current.y,
          z: mobilePos.current.z,
          duration: 0.5,
          ease: "power2.out",
          onComplete: () => {
            currentPos.current = { ...mobilePos.current };
          }
        });
      }
    }
  };

  const adjustCameraForDesktop = () => {
    const desktopState = stateMachine[nextState !== "none" ? nextState : activeCamera];
    const desktopPos = desktopState ? desktopState.camera.position : [0, 1.9, 10];

    if (!inTrans && activeCamera !== "IntroCamera") {
      gsap.to(camera.position, {
        x: desktopPos[0],
        y: desktopPos[1],
        z: desktopPos[2],
        duration: 0.5,
        ease: "power2.out",
        onComplete: () => {
          currentPos.current = {
            x: desktopPos[0],
            y: desktopPos[1],
            z: desktopPos[2]
          };
        }
      });
    }
  };

  useEffect(() => {
    initCamera()
    return () => {
      mixer.removeEventListener("finished", endAnim);
    }
  }, [token])

  useFrame((state, delta) => {
    if (!inTrans && pointer.current && activeCamera !== "IntroCamera") {
      const pointerEffect = isMobile ? 0.1 : 0.5;
      const pointerXEffect = isMobile ? 0.05 : 0.2;

      easing.damp2(
        camera.position,
        [
          Math.sin(pointer.current.x / 2) * -1 * pointerEffect + currentPos.current.x,
          Math.sin(pointer.current.y / 2) * 0.2 * pointerEffect + currentPos.current.y,
          currentPos.current.z,
        ],
        0.5,
        delta,
      );
      easing.damp2(
        camera.rotation,
        [
          currentRot.current.x,
          Math.sin(pointer.current.x / -2) * pointerXEffect + currentRot.current.y,
          currentRot.current.z,
        ],
        0.5,
        delta,
      );
    }
  });

  useGSAP(
    () => {
      if (nextState !== "none") {
        dispatch(setInTrans(true));

        const cameraConfig = isMobile && stateMachine[nextState].mobileCamera
          ? stateMachine[nextState].mobileCamera
          : stateMachine[nextState].camera;

        const pos = cameraConfig.position;
        const quat = new Quaternion(...cameraConfig.quaternion);
        const initialQuat = new Quaternion(...camera.quaternion);

        gsap.to(camera.position, {
          x: pos[0],
          y: pos[1],
          z: pos[2],
          duration: 1.25,
          ease: "back.out(1.4)",
          onComplete: () => {
            dispatch(setInTrans(false));
            dispatch(setCurrentState(nextState));
            currentPos.current = {
              x: pos[0],
              y: pos[1],
              z: pos[2],
            };
            mobilePos.current = {
              x: pos[0],
              y: pos[1],
              z: pos[2],
            };
          },
        });

        const animation = gsap.to(camera.rotation, {
          duration: 0.75,
          ease: "circ.out",
          onUpdate: function () {
            camera.quaternion.slerpQuaternions(initialQuat, quat, animation.progress())
          },
          onComplete: function () {
            const finalRot = camera.rotation;
            currentRot.current = new THREE.Euler(finalRot.x, finalRot.y, finalRot.z);
          }
        });
      }
    },
    { dependencies: [nextState, isMobile] },
  );

  function initCamera() {
    const action = actions.CameraAction;

    if (action) {
      action.clampWhenFinished = true;
      action.loop = LoopOnce;
      action.time = 0;
      action.play();
      action._clip.duration = 170 / 30;
      action.paused = true;

      if (token) {
        setTimeout(() => {
          action.paused = false;
        }, 300);

        mixer.addEventListener("finished", endAnim);
      }
    } else {
      console.error("CameraAction is undefined");
    }
  }

  function endAnim() {
    dispatch(setActiveCamera("MainCamera"));
    dispatch(setCurrentState("Welcome"));
    gsap.to(`.${controlsStyle.controls}`, {
      opacity: 1,
      pointerEvents: "auto",
      duration: 1,
      ease: "power2.out",
    })
  }

  return (
    <PerspectiveCamera
      makeDefault
      name={"Camera"}
      far={100}
      fov={isMobile ? 80 : 50.269}
      position={[0, 1.9, isMobile ? 9 : 7]}
      ref={ref}
    />
  );
});

export default CameraController;
